import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Euro, Plus, Trash2, Pencil, Loader2, X, Check } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", price: "" };

export function TariffsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/tariffs"); setItems(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = () => { setForm(EMPTY); setEditingId(null); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Escribe el nombre del concepto"); return; }
    if (form.price === "" || isNaN(Number(form.price)) || Number(form.price) < 0) { toast.error("Introduce un precio válido"); return; }
    setSaving(true);
    const payload = { name: form.name.trim(), description: form.description.trim(), price: Number(form.price) };
    try {
      if (editingId) await api.patch(`/tariffs/${editingId}`, payload);
      else await api.post("/tariffs", payload);
      toast.success(editingId ? "Concepto actualizado" : "Concepto añadido");
      reset(); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const edit = (t) => { setEditingId(t.id); setForm({ name: t.name, description: t.description || "", price: String(t.price) }); };
  const remove = async (id) => { try { await api.delete(`/tariffs/${id}`); toast.success("Concepto eliminado"); if (editingId === id) reset(); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-bold text-[#373E45] flex items-center gap-2 mb-4"><Euro className="h-5 w-5 text-[#7FD498]" />Conceptos y tarifas</h3>
        {loading ? <Loader2 className="h-6 w-6 animate-spin text-[#7FD498]" /> :
          items.length === 0 ? <p className="text-sm text-[#64748B]">Aún no hay conceptos. Añade el primero en el formulario.</p> :
            <div className="space-y-2" data-testid="tariffs-list">
              {items.map((t) => (
                <div key={t.id} className="flex items-start justify-between gap-3 border border-slate-200 rounded-xl p-3" data-testid={`tariff-${t.id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-[#373E45]">{t.name}</p>
                    {t.description && <p className="text-xs text-[#64748B]">{t.description}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-extrabold text-[#373E45]">{t.price.toFixed(2)} €</span>
                    <button onClick={() => edit(t)} title="Editar" className="text-slate-400 hover:text-[#373E45]" data-testid={`edit-tariff-${t.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(t.id)} title="Eliminar" className="text-slate-400 hover:text-red-600" data-testid={`delete-tariff-${t.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>}
        <p className="text-xs text-slate-400 mt-4">Los importes se indican sin IVA (21%).</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 h-fit">
        <h3 className="font-bold text-[#373E45] mb-4">{editingId ? "Editar concepto" : "Nuevo concepto"}</h3>
        <div className="space-y-3">
          <div>
            <Label className="text-[#373E45] font-semibold text-sm">Nombre del concepto</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej. Ecografía abdominal" className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="tariff-name-input" />
          </div>
          <div>
            <Label className="text-[#373E45] font-semibold text-sm">Descripción (opcional)</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Breve descripción del concepto" className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498] resize-none" data-testid="tariff-description-input" />
          </div>
          <div>
            <Label className="text-[#373E45] font-semibold text-sm">Precio (€, sin IVA)</Label>
            <Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0.00" className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="tariff-price-input" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={saving} className="bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl" data-testid="save-tariff-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{editingId ? <Check className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}{editingId ? "Guardar cambios" : "Añadir concepto"}</>}
            </Button>
            {editingId && <Button onClick={reset} variant="outline" className="rounded-xl border-slate-300" data-testid="cancel-tariff-btn"><X className="h-4 w-4 mr-1" />Cancelar</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}
